import React, { useMemo, useCallback, useEffect } from 'react';
import { getStatusColor, getDrilldown, getNodeDisplay, STATUS_COLORS } from './flow-config';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import './index.scss';

// Suppress ResizeObserver loop error globally (DevExtreme + SVG known issue)
if (typeof window !== 'undefined' && !window._roSuppressed) {
    window._roSuppressed = true;
    let _origError = window.onerror;
    window.onerror = function (msg) {
        if (typeof msg === 'string' && msg.includes('ResizeObserver')) return true;
        if (_origError) return _origError.apply(this, arguments);
    };
    window.addEventListener('error', (e) => {
        if (e.message && e.message.includes('ResizeObserver')) {
            e.stopImmediatePropagation();
            e.preventDefault();
        }
    });
}

// ==========================================
// Mock Data
// ==========================================
var MOCK_FLOW = {
    nodes: [
        { id: 'order', type: 'AROrder', code: 'SO-2026-001', label: 'Đơn hàng', status: 'Release', existence: 'exists' },
        { id: 'issue', type: 'ARIssue', code: 'SI-2026-001', label: 'Phiếu xuất', status: 'Open', existence: 'exists' },
        { id: 'delivery', type: 'ARDelivery', code: 'DV-2026-001', label: 'Giao hàng', status: 'Release', existence: 'exists' },
        { id: 'icissue', type: 'ICIssue', code: 'IC-2026-001', label: 'Xuất kho', status: 'Close', existence: 'exists' },
        { id: 'credit', type: 'ARCredit', code: 'CR-2026-001', label: 'Công nợ', status: 'Close', existence: 'exists' },
        { id: 'payment', type: 'CARequest', code: 'CA-2026-001', label: 'Thanh toán', status: 'New', existence: 'exists' },
        { id: 'invoice', type: 'ARInvoice', code: 'INV-2026-001', label: 'Hoá đơn', status: 'Close', existence: 'exists' }
    ],
    edges: [
        { from: 'order', to: 'issue' },
        { from: 'issue', to: 'delivery' },
        { from: 'delivery', to: 'icissue' },
        { from: 'issue', to: 'credit' },
        { from: 'credit', to: 'invoice' },
        { from: 'order', to: 'payment' }
    ]
};

// ==========================================
// Constants
// ==========================================
const NODE_W = 120;
const NODE_H = 44;
const COL_GAP = 80;
const LANE_GAP = 14;
const PAD_X = 12;
const PAD_Y = 8;
const ARROW_SIZE = 5;

// ==========================================
// Layout Algorithm
// ==========================================
function buildLayout(nodes, edges, opts) {
    if (!nodes || nodes.length === 0) return { positioned: [], edgeLines: [], width: 0, height: 0 };
    if (!opts.showExpected) {
        nodes = nodes.filter((n) => n.existence !== 'expected');
        let nodeIds = new Set(nodes.map((n) => n.id));
        edges = edges.filter((e) => nodeIds.has(e.from) && nodeIds.has(e.to));
    }

    let nodeMap = {};
    for (let n of nodes) nodeMap[n.id] = { ...n };

    // Build adjacency
    let children = {};
    let parents = {};
    for (let e of edges) {
        if (!children[e.from]) children[e.from] = [];
        children[e.from].push(e.to);
        if (!parents[e.to]) parents[e.to] = [];
        parents[e.to].push(e.from);
    }

    // Find root (node with no parents)
    let roots = nodes.filter((n) => !parents[n.id] || parents[n.id].length === 0);
    if (roots.length === 0) roots = [nodes[0]];

    // Column assignment — dùng col từ backend nếu có, fallback time-based
    let colOf = {};
    let _hasBackendCol = nodes.some((n) => n.col !== undefined && n.col !== null);

    if (_hasBackendCol) {
        for (let n of nodes) colOf[n.id] = n.col || 0;
    } else {
        let existNodes = nodes.filter((n) => n.existence !== 'expected');
        let timeValues = [];
        for (let n of existNodes) {
            let t = n.createdAt || 0;
            if (timeValues.indexOf(t) === -1) timeValues.push(t);
        }
        timeValues.sort((a, b) => a - b);
        let timeToCol = {};
        for (let i = 0; i < timeValues.length; i++) timeToCol[timeValues[i]] = i;

        for (let n of existNodes) colOf[n.id] = timeToCol[n.createdAt || 0];
        for (let n of nodes) {
            if (n.existence === 'expected') {
                let pCols = (parents[n.id] || []).map((pid) => colOf[pid]).filter((c) => c !== undefined);
                colOf[n.id] = pCols.length > 0 ? Math.max(...pCols) + 1 : 0;
            }
        }
        let shifted = true;
        while (shifted) {
            shifted = false;
            for (let e of edges) {
                if (colOf[e.from] !== undefined && colOf[e.to] !== undefined && colOf[e.to] <= colOf[e.from]) {
                    colOf[e.to] = colOf[e.from] + 1;
                    shifted = true;
                }
            }
        }
    }

    // Assign lanes — dùng lane từ backend nếu có, fallback BFS
    let laneOf = {};
    let currentLane = 0;
    let _hasBackendLane = nodes.some((n) => n.lane !== undefined && n.lane !== null);

    if (_hasBackendLane) {
        for (let n of nodes) laneOf[n.id] = n.lane || 0;
        for (let n of nodes) { if (laneOf[n.id] > currentLane) currentLane = laneOf[n.id]; }
    } else {
        let queue = roots.map((r) => ({ id: r.id, lane: currentLane }));
        let laneVisited = new Set();

        if (roots.length > 0) {
            laneOf[roots[0].id] = 0;
            laneVisited.add(roots[0].id);
        }

        while (queue.length > 0) {
            let { id, lane } = queue.shift();
            if (laneOf[id] === undefined) laneOf[id] = lane;

            let ch = children[id] || [];
            if (ch.length <= 1) {
                for (let cid of ch) {
                    if (!laneVisited.has(cid)) {
                        laneVisited.add(cid);
                        laneOf[cid] = laneOf[id];
                        queue.push({ id: cid, lane: laneOf[id] });
                    }
                }
            } else {
                let first = true;
                for (let cid of ch) {
                    if (!laneVisited.has(cid)) {
                        laneVisited.add(cid);
                        if (first) {
                            laneOf[cid] = laneOf[id];
                            first = false;
                        } else {
                            currentLane++;
                            laneOf[cid] = currentLane;
                        }
                        queue.push({ id: cid, lane: laneOf[cid] });
                    }
                }
            }
        }

        for (let n of nodes) {
            if (laneOf[n.id] === undefined) {
                currentLane++;
                laneOf[n.id] = currentLane;
            }
        }
    }

    let maxCol = 0;
    for (let id in colOf) {
        if (colOf[id] > maxCol) maxCol = colOf[id];
    }

    // Build positioned nodes
    let totalLanes = currentLane + 1;
    let positioned = [];
    for (let n of nodes) {
        let col = colOf[n.id] !== undefined ? colOf[n.id] : 0;
        let lane = laneOf[n.id] !== undefined ? laneOf[n.id] : 0;
        let x = PAD_X + col * (NODE_W + COL_GAP);
        let y = PAD_Y + lane * (NODE_H + LANE_GAP);
        positioned.push({ ...nodeMap[n.id], x, y, col, lane });
    }

    // ==========================================
    // Edge Routing — Channel-based Minimum Crossing
    // ==========================================
    let posMap = {};
    for (let p of positioned) posMap[p.id] = p;

    // Tính vùng an toàn: dưới tất cả nodes
    let maxNodeY = 0;
    for (let p of positioned) {
        let bot = p.y + NODE_H;
        if (bot > maxNodeY) maxNodeY = bot;
    }
    let belowAllY = maxNodeY + LANE_GAP; // Y phía dưới tất cả nodes

    // Channel X: giữa mỗi cặp cột (gap center)
    function gapX(col) {
        return PAD_X + col * (NODE_W + COL_GAP) - COL_GAP / 2;
    }

    // Đếm số bottom-exit từ mỗi node để offset
    let bottomCount = {};

    let edgeLines = [];
    for (let ei = 0; ei < edges.length; ei++) {
        let e = edges[ei];
        let from = posMap[e.from];
        let to = posMap[e.to];
        if (!from || !to) continue;

        let sameLane = from.lane === to.lane;
        let sameCol = from.col === to.col;
        let dLane = to.lane - from.lane;
        let dCol = to.col - from.col;
        let adjacentLane = Math.abs(dLane) === 1;
        let adjacentCol = Math.abs(dCol) <= 1;

        let segments = [];
        let x1, y1, x2, y2;

        if (sameLane && dCol > 0) {
            // === CÙNG LANE, đi sang phải → đường ngang thẳng ===
            x1 = from.x + NODE_W; y1 = from.y + NODE_H / 2;
            x2 = to.x; y2 = to.y + NODE_H / 2;
            segments.push({ x1, y1, x2, y2 });

        } else {
            // === XA NHAU → Thử nhiều routes, chọn ít crossing nhất ===
            let bIdx = bottomCount[e.from] || 0;
            bottomCount[e.from] = bIdx + 1;

            x2 = to.x;
            y2 = to.y + NODE_H / 2;

            // Sinh candidates — 4 exit ports × enter ports hợp lệ
            let candidates = [];
            let offset = bIdx * 10;

            // Port positions
            let fromR = { x: from.x + NODE_W, y: from.y + NODE_H / 2 };
            let fromB = { x: from.x + NODE_W / 2 + offset, y: from.y + NODE_H };
            let fromT = { x: from.x + NODE_W / 2 + offset, y: from.y };
            let fromL = { x: from.x, y: from.y + NODE_H / 2 };
            let toL = { x: to.x, y: to.y + NODE_H / 2 };
            let toT = { x: to.x + NODE_W / 2, y: to.y };
            let toR = { x: to.x + NODE_W, y: to.y + NODE_H / 2 };
            let toB = { x: to.x + NODE_W / 2, y: to.y + NODE_H };
            let channelY = belowAllY + offset;
            let aboveY = PAD_Y - 10 - offset; // kênh phía trên

            // R→L: ngang → gap xuống → ngang
            let gX = gapX(Math.min(from.col + 1, to.col));
            candidates.push({ segs: [
                { x1: fromR.x, y1: fromR.y, x2: gX, y2: fromR.y },
                { x1: gX, y1: fromR.y, x2: gX, y2: toL.y },
                { x1: gX, y1: toL.y, x2: toL.x, y2: toL.y }
            ], sx: fromR.x, sy: fromR.y });

            // B→L: xuống → ngang
            candidates.push({ segs: [
                { x1: fromB.x, y1: fromB.y, x2: fromB.x, y2: toL.y },
                { x1: fromB.x, y1: toL.y, x2: toL.x, y2: toL.y }
            ], sx: fromB.x, sy: fromB.y });

            // B→T thẳng đứng (cùng cột hoặc gần): xuống thẳng tới target
            if (Math.abs(dCol) <= 1 && dLane > 0) {
                candidates.push({ segs: [
                    { x1: fromB.x, y1: fromB.y, x2: toT.x, y2: toT.y }
                ], sx: fromB.x, sy: fromB.y });
            }

            // B→T U-shape: xuống → ngang dưới → lên
            candidates.push({ segs: [
                { x1: fromB.x, y1: fromB.y, x2: fromB.x, y2: channelY },
                { x1: fromB.x, y1: channelY, x2: toT.x, y2: channelY },
                { x1: toT.x, y1: channelY, x2: toT.x, y2: toT.y }
            ], sx: fromB.x, sy: fromB.y });

            // R→T: ngang → xuống tới target
            candidates.push({ segs: [
                { x1: fromR.x, y1: fromR.y, x2: toT.x, y2: fromR.y },
                { x1: toT.x, y1: fromR.y, x2: toT.x, y2: toT.y }
            ], sx: fromR.x, sy: fromR.y });

            // B→L qua channel dưới: xuống dưới tất cả → ngang → lên → vào
            let enterX = to.x - 12 - bIdx * 6;
            if (enterX < fromB.x + 5) enterX = fromB.x + 5;
            candidates.push({ segs: [
                { x1: fromB.x, y1: fromB.y, x2: fromB.x, y2: channelY },
                { x1: fromB.x, y1: channelY, x2: enterX, y2: channelY },
                { x1: enterX, y1: channelY, x2: enterX, y2: toL.y },
                { x1: enterX, y1: toL.y, x2: toL.x, y2: toL.y }
            ], sx: fromB.x, sy: fromB.y });

            // T→L qua channel trên (nếu target ở dưới): lên trên → ngang → xuống → vào
            if (dLane > 0 && aboveY > 0) {
                candidates.push({ segs: [
                    { x1: fromT.x, y1: fromT.y, x2: fromT.x, y2: aboveY },
                    { x1: fromT.x, y1: aboveY, x2: enterX, y2: aboveY },
                    { x1: enterX, y1: aboveY, x2: enterX, y2: toL.y },
                    { x1: enterX, y1: toL.y, x2: toL.x, y2: toL.y }
                ], sx: fromT.x, sy: fromT.y });
            }

            // Score: đếm crossing với edges đã route + đếm node overlap
            let bestIdx = 0;
            let bestScore = Infinity;

            for (let ci = 0; ci < candidates.length; ci++) {
                let c = candidates[ci];
                let score = 0;

                // Crossing với edges đã route
                for (let prevEdge of edgeLines) {
                    if (!prevEdge.segments) continue;
                    for (let cs of c.segs) {
                        let csH = Math.abs(cs.y1 - cs.y2) < 1;
                        for (let ps of prevEdge.segments) {
                            let psH = Math.abs(ps.y1 - ps.y2) < 1;
                            if (csH === psH) continue;
                            let h = csH ? cs : ps;
                            let v = csH ? ps : cs;
                            let hY = h.y1, hMinX = Math.min(h.x1, h.x2), hMaxX = Math.max(h.x1, h.x2);
                            let vX = v.x1, vMinY = Math.min(v.y1, v.y2), vMaxY = Math.max(v.y1, v.y2);
                            if (vX > hMinX && vX < hMaxX && hY > vMinY && hY < vMaxY) score += 10;
                        }
                    }
                }

                // Node overlap
                for (let cs of c.segs) {
                    let csH = Math.abs(cs.y1 - cs.y2) < 1;
                    for (let n of positioned) {
                        if (n.id === e.from || n.id === e.to) continue;
                        let nL = n.x - 3, nR = n.x + NODE_W + 3, nT = n.y - 3, nB = n.y + NODE_H + 3;
                        if (csH) {
                            let y = cs.y1, minX = Math.min(cs.x1, cs.x2), maxX = Math.max(cs.x1, cs.x2);
                            if (y > nT && y < nB && maxX > nL && minX < nR) score += 20;
                        } else {
                            let x = cs.x1, minY = Math.min(cs.y1, cs.y2), maxY = Math.max(cs.y1, cs.y2);
                            if (x > nL && x < nR && maxY > nT && minY < nB) score += 20;
                        }
                    }
                }

                if (score < bestScore) { bestScore = score; bestIdx = ci; }
            }

            let best = candidates[bestIdx];
            segments = best.segs;
            x1 = best.sx;
            y1 = best.sy;
        }

        edgeLines.push({ ...e, x1: segments[0].x1, y1: segments[0].y1, x2, y2, segments });
    }

    let width = PAD_X * 2 + (maxCol + 1) * (NODE_W + COL_GAP) - COL_GAP;
    let height = Math.max(
        PAD_Y * 2 + totalLanes * (NODE_H + LANE_GAP) - LANE_GAP,
        belowAllY + Object.keys(bottomCount).length * 8 + PAD_Y // đủ chỗ cho channel routing
    );

    return { positioned, edgeLines, width, height };
}

// ==========================================
// SVG Renderers
// ==========================================
function calcEdgePath(e) {
    if (e.segments && e.segments.length > 0) {
        let d = `M ${e.segments[0].x1} ${e.segments[0].y1}`;
        for (let s of e.segments) d += ` L ${s.x2} ${s.y2}`;
        return d;
    }
    return `M ${e.x1} ${e.y1} L ${e.x2} ${e.y2}`;
}

function translateText(obj) {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return AntLocalization.translateText(obj) || obj.vi || obj.en || '';
}

// ==========================================
// Component
// ==========================================
export default function AntVoucherFlow(props) {
    let {
        flowData,
        currentNode,
        currentCode,
        onNodeClick,
        showExpected = true,
        compact = false,
        configOverride,
        accessScreens,
        mockMode = false,
        visible,
        session
    } = props;

    // ─── Visibility check via AntCondition ────────────────────
    if (visible && session) {
        var isVis = AntCondition.checkAll({}, visible, session);
        if (!isVis) return null;
    }

    // ─── Mock mode: use MOCK_FLOW instead of props.flowData ───
    if (mockMode) flowData = MOCK_FLOW;

    // Merge config override
    // configOverride = {
    //   nodeColors: { 'AROrder': { border, dot }, 'ICIssue': { border, dot }, ... },
    //   statusColors: { 'Cancel': { bg, border, dot, icon }, ... },
    //   drilldown: { 'ARInvoice': { url }, ... }
    // }
    // Ưu tiên: warningLabel > nodeColors[type] merge status > statusColors > default
    let overrideNodeColors = configOverride && configOverride.nodeColors;
    let overrideStatusColors = configOverride && configOverride.statusColors;
    let overrideDrilldown = configOverride && configOverride.drilldown;

    let getColor = useCallback(
        (status, type, existence, disabled, node) => {
            // 1. warningLabel → warning style
            if (node && node.warningLabel) return STATUS_COLORS._warning;
            // 2. Default status color
            let base = getStatusColor(status, type, existence, disabled);
            // 3. Merge node type color (border/dot đặc trưng phiếu)
            if (overrideNodeColors && overrideNodeColors[type]) {
                base = { ...base, ...overrideNodeColors[type] };
            }
            // 4. Status override CUỐI CÙNG (Cancel/Reject ghi đè nodeColors)
            if (overrideStatusColors && overrideStatusColors[status]) {
                base = { ...base, ...overrideStatusColors[status] };
            }
            return base;
        },
        [overrideNodeColors, overrideStatusColors]
    );

    // Layout — bọc try/catch để không crash app
    let layout = useMemo(() => {
        if (!flowData || !flowData.nodes) return { positioned: [], edgeLines: [], width: 400, height: 60 };
        try {
            return buildLayout(flowData.nodes, flowData.edges || [], { showExpected });
        } catch (e) {
            console.error('[VoucherFlow] Layout error:', e);
            return { positioned: [], edgeLines: [], width: 400, height: 60 };
        }
    }, [flowData, showExpected]);

    let svgHeight = Math.max(layout.height, 60);
    let svgWidth = Math.max(layout.width, 300);

    // Node click handler
    let handleNodeClick = useCallback(
        (node) => {
            if (node.disabled) return;
            if (node.existence !== 'exists') return;
            if (!node.code) return;
            if (onNodeClick) onNodeClick(node);
        },
        [onNodeClick]
    );

    if (!flowData || !flowData.nodes || flowData.nodes.length <= 1) {
        return null;
    }

    // Render arrow markers
    let renderDefs = () => (
        <defs>
            <marker id="flow-arrow-normal" viewBox="0 0 10 10" refX="9" refY="5" markerWidth={ARROW_SIZE} markerHeight={ARROW_SIZE} orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#94A3B8" />
            </marker>
            <marker id="flow-arrow-expected" viewBox="0 0 10 10" refX="9" refY="5" markerWidth={ARROW_SIZE} markerHeight={ARROW_SIZE} orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#CBD5E1" />
            </marker>
        </defs>
    );

    // Render edge
    let renderEdge = (e, i) => {
        let isExpected = e.existence === 'expected';
        let markerEnd = isExpected ? 'url(#flow-arrow-expected)' : 'url(#flow-arrow-normal)';
        let color = isExpected ? STATUS_COLORS._expected.border : '#94A3B8';
        let path = calcEdgePath(e);

        return (
            <g key={'e_' + i} className="flow-edge">
                <path d={path} stroke={color} strokeWidth={isExpected ? 1 : 1.5} strokeDasharray={isExpected ? '5,5' : 'none'} fill="none" markerEnd={markerEnd} />
            </g>
        );
    };

    // Render node — chỉ label + status
    let renderNode = (node, i) => {
        let display = getNodeDisplay(node.type);
        let isCurrentNode = currentNode === node.type && currentCode === node.code;
        let colors = getColor(node.status, node.type, node.existence, node.disabled, node);
        let currentStyle = isCurrentNode ? STATUS_COLORS._current : null;
        let drilldown = getDrilldown(node.type);
        if (overrideDrilldown && overrideDrilldown[node.type]) {
            drilldown = { ...drilldown, ...overrideDrilldown[node.type] };
        }
        let _drillUrl = node.drilldownUrl || drilldown.url;
        let _hasScreenAccess = !drilldown.whenUserScreen || !accessScreens || !Array.isArray(accessScreens) || accessScreens.length === 0 || accessScreens.includes(drilldown.whenUserScreen);
        let isClickable = node.disabled !== true && node.noAccess !== true && _hasScreenAccess && node.existence === 'exists' && node.code && _drillUrl && (node.drilldownUrl || drilldown.clickable !== false);
        let isPulse = colors.pulse;

        let className = 'flow-node';
        if (isClickable) className += ' clickable';
        if (node.disabled === true) className += ' disabled';
        if (isPulse) className += ' flow-node-pulse';

        let w = compact ? 100 : NODE_W;
        let h = compact ? 36 : NODE_H;

        let statusLabel = node.statusLabel ? translateText(node.statusLabel) : null;

        return (
            <g key={'n_' + node.id} className={className} transform={`translate(${node.x}, ${node.y})`} onClick={() => handleNodeClick(node)}>
                {/* Card background */}
                <rect
                    className="node-bg"
                    x={0}
                    y={0}
                    width={w}
                    height={h}
                    rx={2}
                    ry={2}
                    fill={colors.bg}
                    stroke={colors.border}
                    strokeWidth={1}
                    strokeDasharray={colors.dashBorder ? '4,3' : 'none'}
                    style={currentStyle ? { filter: `drop-shadow(${currentStyle.shadow})` } : undefined}
                    opacity={colors.opacity || 1}
                />

                {/* Label */}
                <text className="flow-node-label" x={8} y={statusLabel ? 16 : h / 2 + 4} fill={colors.text}>
                    {translateText(node.label)}
                    {node.count > 1 ? ` ×${node.count}` : ''}
                </text>

                {/* Status: icon (nếu có) hoặc dot + label */}
                {statusLabel && (
                    <g>
                        {colors.icon ? (
                            <text x={12} y={34} fill={colors.iconColor || colors.dot} fontSize={12} textAnchor="middle" fontWeight="bold">
                                {colors.icon}
                            </text>
                        ) : (
                            <circle cx={12} cy={30} r={3} fill={colors.dot} />
                        )}
                        <text className="flow-node-status" x={20} y={33} fill={colors.text} opacity={0.75}>
                            {statusLabel}
                        </text>
                    </g>
                )}

                {/* Progress bar for DepositGate */}
                {display.customRender === 'progressBar' && node.meta && (
                    <g>
                        <rect className="flow-progress-bar-bg" x={8} y={h - 12} width={w - 16} height={5} />
                        <rect
                            className="flow-progress-bar-fill"
                            x={8}
                            y={h - 12}
                            width={Math.min(((node.meta.deposit_progress || 0) / 100) * (w - 16), w - 16)}
                            height={5}
                            fill={node.meta.deposit_progress >= 100 ? '#10B981' : '#3B82F6'}
                        />
                        <text className="flow-progress-text" x={w / 2} y={h - 16} fill={colors.text} textAnchor="middle">
                            {node.meta.deposit_progress || 0}%
                        </text>
                    </g>
                )}
            </g>
        );
    };

    return (
        <svg className="ant-voucher-flow-svg" viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%" height={svgHeight} preserveAspectRatio="xMinYMin meet" style={{ maxHeight: svgHeight, display: 'block' }}>
            {renderDefs()}
            {layout.edgeLines.map(renderEdge)}
            {layout.positioned.map(renderNode)}
        </svg>
    );
}
